/**
 * WISHLIST-STORE-3 — Offer Opportunity Mock Inbox (admin/staff only · MOCK) · Jun 2026.
 *
 * STRICT INVARIANTS (do not relax without explicit nominal GO):
 *   • Admin + staff only (RBAC: AdminLayout sidebar + QA_MANAGER_DENIED_PREFIXES
 *     `/admin/gear` covers qa_manager · defensive guard inside the page hard-
 *     redirects every other role).
 *   • Zero DB writes · zero API calls · 100% mock data inline.
 *   • Zero customer-facing surface · zero ConsentGate · zero consent capture ·
 *     zero notifications real · zero billing real · zero marketplace activation
 *     · zero checkout · zero outreach.
 *   • Every action button is disabled with a `mock_only` / `blocked` badge.
 *
 * Closes documentary Phase 2 of GEAR-WISHLIST: visualises how the future
 * Offer Opportunity Inbox will look BEFORE any runtime exists. Aligned with
 * GEAR-1 (collections `shop_offer_opportunities` / `shop_offer_events`),
 * WISHLIST-STORE-2 (permission state machine) and GEAR-AUDIT-1 (event
 * catalogue).
 */
import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import {
  ShieldAlert, Lock, Inbox, FileCheck, Ban, Eye, ClipboardList,
  AlertTriangle, Scale,
} from "lucide-react";

// QA-I18N-ADMIN-ES-FULL-1 — bilingual copy for this admin-only mock page.
// Slug-style contract keys (status_codes, event_types, capabilities, reason_codes,
// permission_states, intent values) are PRESERVED in English on purpose because
// they document the future backend contract names — translating them would lie
// about what the runtime would emit. Only free-form prose/labels are localized.
const COPY = {
  en: {
    pageTitle: "Offer Opportunity Inbox (WISHLIST-STORE-3)",
    pageSubtitle: "Admin/Staff only · 100% mock · zero writes · zero real notifications · zero checkout · zero customer-facing.",
    bannerTitle: "Mock only · no real offer · no notification · no marketplace checkout.",
    bannerBody: "Mock screen to visualise and validate the future inbox of opportunities derived from My Gear / Wishlist / Store Access. Inline data (no DB, no API). Closes documentary Phase 2 of GEAR-WISHLIST alongside WISHLIST-STORE-1 (Store Access Settings) and WISHLIST-STORE-2 (Permission Model · Counsel Packet).",
    section1Title: "1. Opportunity inbox (mock)",
    section1Subtitle: (n) => `${n} simulated opportunities · click to open detail`,
    section2Title: "2. Opportunity status catalogue (mock)",
    section2Subtitle: "10 states modelled before any runtime",
    section3Title: "3. Blocked actions matrix",
    section3Subtitle: "All real buttons disabled / no-op · only mock_only on create_offer",
    section4Title: "4. Opportunity detail (mock)",
    section4SubtitleSelected: (id) => `Selected: ${id}`,
    section5Title: "5. Audit preview · GEAR-AUDIT-1 alignment",
    section5Subtitle: "6 event types that the future runtime WOULD emit",
    section6Title: "6. Counsel / legal hint",
    section6Subtitle: "What counsel must sign off BEFORE any runtime activation",
    th_user: "User", th_shop: "Shop", th_wishlistItem: "Wishlist item", th_intent: "Intent",
    th_priority: "Priority", th_status: "Status", th_action: "Action", th_reason: "Reason",
    th_future_event: "Future event", th_channel: "Channel", th_reason_code: "Reason code",
    openBtn: "Open",
    detail_wishlist: "Wishlist item", detail_category: "Category", detail_desired_sku: "Desired SKU mock",
    detail_related_gear: "Related gear / racket", detail_intent_prefix: "Intent",
    detail_shop_eligibility: "Shop eligibility", detail_permission: "Permission · Consent",
    detail_consent_prefix: "consent", detail_reason_code: "reason_code",
    detail_audit_target_title: "Audit event that WOULD be emitted (no real audit today)",
    detail_audit_not_written: "NOT written to `gear_access_audit_log` · collection absent · zero writes.",
    section5_disclaimer: "Mock preview only — no audit row is created · `gear_access_audit_log` collection still absent.",
    notes: {
      mock_detected: "Heuristic detected wishlist↔stock match (mock)",
      eligible_pending_permission: "User has not granted shop permission yet",
      eligible_permission_granted_future: "Would be actionable once runtime + ConsentGate exist",
      blocked_no_permission: "No `shop_user_wishlist_permissions` row in granted state",
      blocked_no_consent: "ConsentGate undeployed · no consent_version_ref",
      blocked_minor_policy: "Target user is minor without verified guardian",
      blocked_country_policy: "Country legal_zone blocks cross-border data transfer",
      blocked_shop_not_paid: "Shop entitlement expired/disabled · paid runtime blocked",
      mock_offer_ready: "Mock payload composed · NOT sent · NOT persisted",
      mock_archived: "Dismissed in mock inbox · no audit emitted",
    },
    blockedReasons: {
      create_offer: "GEAR-3 / WISHLIST-STORE-3 mock surfaces only · is_mock=true",
      send_offer: "No real offer runtime · awaiting nominal GO",
      send_notification: "Resend/WhatsApp/SMS/push all in MOCK MODE",
      checkout: "Marketplace + billing activation hard-blocked",
      contact_user: "Customer-facing outreach hard-blocked",
      export: "External export hard-blocked (GEAR-AUDIT-1 rule)",
    },
    counselHints: [
      "Requires valid `shop_user_wishlist_permissions` row in `granted` state (WISHLIST-STORE-2 state machine).",
      "Requires ConsentGate deployed + consent_version_ref captured · legal_basis_ref recorded per jurisdiction.",
      "Requires shop entitlement (Store Access trial / paid / partner-paid · WISHLIST-STORE-1).",
      "Requires real notification policy + per-channel quiet hours · all channels currently MOCK MODE.",
      "Requires marketplace/billing GO if checkout/payment is involved · checkout HARD-BLOCKED today.",
      "Customer-facing activation remains blocked until explicit nominal GO.",
    ],
    footerLegal: "Legal package accepted / counsel OK for authorized internal/admin-only technical work. Public activation remains blocked until explicit nominal GO.",
  },
  es: {
    pageTitle: "Bandeja de oportunidades de oferta (WISHLIST-STORE-3)",
    pageSubtitle: "Sólo admin/staff · 100% mock · cero escrituras · cero notificaciones reales · cero checkout · cero customer-facing.",
    bannerTitle: "Sólo mock · sin oferta real · sin notificación · sin checkout marketplace.",
    bannerBody: "Pantalla mock para visualizar y validar la futura bandeja de oportunidades derivadas de My Gear / Wishlist / Store Access. Datos inline (sin DB, sin API). Cierra Fase 2 documental de GEAR-WISHLIST junto con WISHLIST-STORE-1 (Store Access Settings) y WISHLIST-STORE-2 (Modelo de permisos · Counsel Packet).",
    section1Title: "1. Bandeja de oportunidades (mock)",
    section1Subtitle: (n) => `${n} oportunidades simuladas · click para abrir detalle`,
    section2Title: "2. Catálogo de estados de oportunidad (mock)",
    section2Subtitle: "10 estados modelados antes de cualquier runtime",
    section3Title: "3. Matriz de acciones bloqueadas",
    section3Subtitle: "Todos los botones reales deshabilitados / sin efecto · sólo mock_only en create_offer",
    section4Title: "4. Detalle de oportunidad (mock)",
    section4SubtitleSelected: (id) => `Seleccionada: ${id}`,
    section5Title: "5. Preview de auditoría · alineación con GEAR-AUDIT-1",
    section5Subtitle: "6 tipos de evento que el futuro runtime EMITIRÍA",
    section6Title: "6. Pista legal / counsel",
    section6Subtitle: "Lo que counsel debe firmar ANTES de cualquier activación del runtime",
    th_user: "Usuario", th_shop: "Tienda", th_wishlistItem: "Ítem de wishlist", th_intent: "Intención",
    th_priority: "Prioridad", th_status: "Estado", th_action: "Acción", th_reason: "Motivo",
    th_future_event: "Evento futuro", th_channel: "Canal", th_reason_code: "Código de motivo",
    openBtn: "Abrir",
    detail_wishlist: "Ítem de wishlist", detail_category: "Categoría", detail_desired_sku: "SKU deseado (mock)",
    detail_related_gear: "Equipamiento / raqueta relacionada", detail_intent_prefix: "Intención",
    detail_shop_eligibility: "Elegibilidad de tienda", detail_permission: "Permiso · Consentimiento",
    detail_consent_prefix: "consentimiento", detail_reason_code: "código_motivo",
    detail_audit_target_title: "Evento de auditoría que se EMITIRÍA (sin auditoría real hoy)",
    detail_audit_not_written: "NO se escribe en `gear_access_audit_log` · colección ausente · cero escrituras.",
    section5_disclaimer: "Preview mock solamente — no se crea ninguna fila de auditoría · la colección `gear_access_audit_log` sigue ausente.",
    notes: {
      mock_detected: "Heurística detectó coincidencia wishlist↔stock (mock)",
      eligible_pending_permission: "El usuario aún no ha otorgado permiso a la tienda",
      eligible_permission_granted_future: "Sería ejecutable una vez existan runtime + ConsentGate",
      blocked_no_permission: "Sin fila en `shop_user_wishlist_permissions` en estado granted",
      blocked_no_consent: "ConsentGate no desplegado · sin consent_version_ref",
      blocked_minor_policy: "Usuario destino es menor sin tutor verificado",
      blocked_country_policy: "El legal_zone del país bloquea transferencia transfronteriza",
      blocked_shop_not_paid: "Entitlement de tienda expirado/deshabilitado · runtime de pago bloqueado",
      mock_offer_ready: "Payload mock compuesto · NO enviado · NO persistido",
      mock_archived: "Descartada en bandeja mock · sin auditoría emitida",
    },
    blockedReasons: {
      create_offer: "GEAR-3 / WISHLIST-STORE-3 sólo superficies mock · is_mock=true",
      send_offer: "Sin runtime real de oferta · esperando GO nominal",
      send_notification: "Resend/WhatsApp/SMS/push todos en MOCK MODE",
      checkout: "Activación de marketplace + billing hard-bloqueada",
      contact_user: "Contacto customer-facing hard-bloqueado",
      export: "Export externo hard-bloqueado (regla GEAR-AUDIT-1)",
    },
    counselHints: [
      "Requiere fila válida en `shop_user_wishlist_permissions` en estado `granted` (máquina de estados WISHLIST-STORE-2).",
      "Requiere ConsentGate desplegado + consent_version_ref capturado · legal_basis_ref registrado por jurisdicción.",
      "Requiere entitlement de tienda (Store Access trial / paid / partner-paid · WISHLIST-STORE-1).",
      "Requiere política real de notificaciones + quiet hours por canal · todos los canales actualmente en MOCK MODE.",
      "Requiere GO de marketplace/billing si hay checkout/pago involucrado · checkout HARD-BLOQUEADO hoy.",
      "La activación customer-facing permanece bloqueada hasta GO nominal explícito.",
    ],
    footerLegal: "Legal package accepted / counsel OK for authorized internal/admin-only technical work. Public activation remains blocked until explicit nominal GO.",
  },
};

const OPPORTUNITY_STATUSES = [
  { key: "mock_detected", tone: "slate", note: "Heuristic detected wishlist↔stock match (mock)" },
  { key: "eligible_pending_permission", tone: "amber", note: "User has not granted shop permission yet" },
  { key: "eligible_permission_granted_future", tone: "emerald", note: "Would be actionable once runtime + ConsentGate exist" },
  { key: "blocked_no_permission", tone: "rose", note: "No `shop_user_wishlist_permissions` row in granted state" },
  { key: "blocked_no_consent", tone: "rose", note: "ConsentGate undeployed · no consent_version_ref" },
  { key: "blocked_minor_policy", tone: "rose", note: "Target user is minor without verified guardian" },
  { key: "blocked_country_policy", tone: "rose", note: "Country legal_zone blocks cross-border data transfer" },
  { key: "blocked_shop_not_paid", tone: "rose", note: "Shop entitlement expired/disabled · paid runtime blocked" },
  { key: "mock_offer_ready", tone: "indigo", note: "Mock payload composed · NOT sent · NOT persisted" },
  { key: "mock_archived", tone: "slate", note: "Dismissed in mock inbox · no audit emitted" },
];

const BLOCKED_ACTIONS = [
  { action: "create_offer", status: "mock_only", reason: "GEAR-3 / WISHLIST-STORE-3 mock surfaces only · is_mock=true" },
  { action: "send_offer", status: "blocked", reason: "No real offer runtime · awaiting nominal GO" },
  { action: "send_notification", status: "blocked", reason: "Resend/WhatsApp/SMS/push all in MOCK MODE" },
  { action: "checkout", status: "blocked", reason: "Marketplace + billing activation hard-blocked" },
  { action: "contact_user", status: "blocked", reason: "Customer-facing outreach hard-blocked" },
  { action: "export", status: "blocked", reason: "External export hard-blocked (GEAR-AUDIT-1 rule)" },
];

const AUDIT_PREVIEW_EVENTS = [
  { event: "shop_create_offer_opportunity", channel: "shop_console", reason_code: "wishlist_offer" },
  { event: "shop_update_offer_opportunity", channel: "shop_console", reason_code: "wishlist_offer" },
  { event: "access_denied", channel: "shop_console", reason_code: "blocked_no_permission" },
  { event: "policy_blocked", channel: "shop_console", reason_code: "blocked_country_policy" },
  { event: "permission_required", channel: "shop_console", reason_code: "blocked_no_permission" },
  { event: "consent_required", channel: "shop_console", reason_code: "blocked_consent_gate_undeployed" },
];

const COUNSEL_HINTS = [
  "Requires valid `shop_user_wishlist_permissions` row in `granted` state (WISHLIST-STORE-2 state machine).",
  "Requires ConsentGate deployed + consent_version_ref captured · legal_basis_ref recorded per jurisdiction.",
  "Requires shop entitlement (Store Access trial / paid / partner-paid · WISHLIST-STORE-1).",
  "Requires real notification policy + per-channel quiet hours · all channels currently MOCK MODE.",
  "Requires marketplace/billing GO if checkout/payment is involved · checkout HARD-BLOCKED today.",
  "Customer-facing activation remains blocked until explicit nominal GO.",
];

// Mock inbox — 8 representative opportunities spanning every status.
const OPPORTUNITIES = [
  {
    id: "OFF-MOCK-001",
    user_mock: "USR-MOCK-ES-001 · padel · ES",
    user_role: "player (mock)",
    shop_id: "SHOP-MOCK-ES-MADRID",
    shop_name: "Demo Padel & Tennis Madrid",
    wishlist_item: "Bullpadel Vertex 04 Control 2024",
    product_category: "padel_racket",
    desired_product: "BULLPADEL-VERTEX-04-CONTROL-2024-PRO",
    priority: "high",
    intent: "wants_to_buy",
    eligibility: "eligible",
    permission_state: "eligible_pending_permission",
    consent_state: "pending_consentgate",
    shop_entitlement: "trial_active",
    status: "eligible_pending_permission",
    reason_code: "awaiting_consent_gate",
    detected_at: "2026-06-13T09:22:00Z",
    related_gear: "User racket profile · 2× Bullpadel Vertex 03",
    audit_target_event: "shop_create_offer_opportunity",
  },
  {
    id: "OFF-MOCK-002",
    user_mock: "USR-MOCK-IT-014 · tennis · IT",
    user_role: "player (mock)",
    shop_id: "SHOP-MOCK-IT-MILAN",
    shop_name: "Milano Racket Studio",
    wishlist_item: "Luxilon Alu Power 125 string set",
    product_category: "tennis_string",
    desired_product: "LUXILON-ALU-POWER-125",
    priority: "medium",
    intent: "needs_replacement",
    eligibility: "blocked",
    permission_state: "blocked_shop_not_paid",
    consent_state: "not_required_internal_mock",
    shop_entitlement: "expired",
    status: "blocked_shop_not_paid",
    reason_code: "blocked_service_state_disabled",
    detected_at: "2026-06-12T16:11:00Z",
    related_gear: "Wilson Pro Staff 97 v14 · current tension 24/22 kg",
    audit_target_event: "access_denied",
  },
  {
    id: "OFF-MOCK-003",
    user_mock: "USR-MOCK-US-077 · tennis · US",
    user_role: "player (mock · minor)",
    shop_id: "SHOP-MOCK-US-NYC",
    shop_name: "NYC Internal Beta Lab",
    wishlist_item: "Babolat Pure Aero Junior 26",
    product_category: "tennis_racket",
    desired_product: "BABOLAT-PURE-AERO-JR-26-2025",
    priority: "high",
    intent: "wants_to_try",
    eligibility: "blocked",
    permission_state: "blocked_minor_policy",
    consent_state: "blocked_by_minor_policy",
    shop_entitlement: "internal_beta",
    status: "blocked_minor_policy",
    reason_code: "blocked_role_scope",
    detected_at: "2026-06-12T11:45:00Z",
    related_gear: "Minor profile — guardian consent missing",
    audit_target_event: "policy_blocked",
  },
  {
    id: "OFF-MOCK-004",
    user_mock: "USR-MOCK-FR-022 · tennis · FR",
    user_role: "player (mock)",
    shop_id: "SHOP-MOCK-FR-LYON",
    shop_name: "Lyon Tennis Atelier",
    wishlist_item: "Yonex VCORE 100 (300g) 2024",
    product_category: "tennis_racket",
    desired_product: "YONEX-VCORE-100-300-2024",
    priority: "medium",
    intent: "compare_later",
    eligibility: "blocked",
    permission_state: "blocked_country_policy",
    consent_state: "blocked_by_country_policy",
    shop_entitlement: "expired",
    status: "blocked_country_policy",
    reason_code: "blocked_country_policy",
    detected_at: "2026-06-11T18:30:00Z",
    related_gear: "Wilson Clash 100 v2 · 280 g",
    audit_target_event: "policy_blocked",
  },
  {
    id: "OFF-MOCK-005",
    user_mock: "USR-MOCK-ES-103 · padel · ES",
    user_role: "player (mock)",
    shop_id: "SHOP-MOCK-ES-VALENCIA",
    shop_name: "Valencia Padel Hub",
    wishlist_item: "Adidas Adipower Multiweight 2024",
    product_category: "padel_racket",
    desired_product: "ADIDAS-ADIPOWER-MULTIWEIGHT-2024",
    priority: "high",
    intent: "wants_to_buy",
    eligibility: "eligible",
    permission_state: "eligible_permission_granted_future",
    consent_state: "granted_future",
    shop_entitlement: "paid_active",
    status: "mock_offer_ready",
    reason_code: "wishlist_offer",
    detected_at: "2026-06-10T10:01:00Z",
    related_gear: "Adidas Metalbone 3.2 · grip L2",
    audit_target_event: "shop_create_offer_opportunity",
  },
  {
    id: "OFF-MOCK-006",
    user_mock: "USR-MOCK-ES-208 · tennis · ES",
    user_role: "player (mock)",
    shop_id: "SHOP-MOCK-ES-BARCELONA",
    shop_name: "Barcelona Stringing Co.",
    wishlist_item: "Solinco Hyper-G Soft 1.25",
    product_category: "tennis_string",
    desired_product: "SOLINCO-HYPER-G-SOFT-125",
    priority: "low",
    intent: "needs_replacement",
    eligibility: "pending",
    permission_state: "blocked_no_permission",
    consent_state: "pending_consentgate",
    shop_entitlement: "pending_approval",
    status: "blocked_no_permission",
    reason_code: "blocked_no_permission",
    detected_at: "2026-06-09T14:55:00Z",
    related_gear: "Head Speed MP 2024 · tension 23/21 kg",
    audit_target_event: "permission_required",
  },
  {
    id: "OFF-MOCK-007",
    user_mock: "USR-MOCK-ES-001 · padel · ES",
    user_role: "player (mock)",
    shop_id: "SHOP-MOCK-ES-MADRID",
    shop_name: "Demo Padel & Tennis Madrid",
    wishlist_item: "Black Crown Genesis 5.0 grip",
    product_category: "padel_grip",
    desired_product: "BLACK-CROWN-GENESIS-50-GRIP",
    priority: "medium",
    intent: "wants_to_try",
    eligibility: "blocked",
    permission_state: "blocked_no_consent",
    consent_state: "pending_consentgate",
    shop_entitlement: "trial_active",
    status: "blocked_no_consent",
    reason_code: "blocked_consent_gate_undeployed",
    detected_at: "2026-06-08T08:14:00Z",
    related_gear: "Bullpadel Vertex 03 — handle wrap worn",
    audit_target_event: "consent_required",
  },
  {
    id: "OFF-MOCK-008",
    user_mock: "USR-MOCK-ES-042 · tennis · ES",
    user_role: "player (mock)",
    shop_id: "SHOP-MOCK-ES-MADRID",
    shop_name: "Demo Padel & Tennis Madrid",
    wishlist_item: "Tecnifibre Razor Code 1.25",
    product_category: "tennis_string",
    desired_product: "TECNIFIBRE-RAZOR-CODE-125",
    priority: "low",
    intent: "compare_later",
    eligibility: "n/a",
    permission_state: "not_requested",
    consent_state: "not_required_internal_mock",
    shop_entitlement: "trial_active",
    status: "mock_archived",
    reason_code: "qa_test",
    detected_at: "2026-06-07T20:02:00Z",
    related_gear: "Tecnifibre TFight 305 · 320 g strung",
    audit_target_event: "mock_emit",
  },
];

const Pill = ({ tone = "slate", testId, children }) => {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-800",
    amber: "bg-amber-100 text-amber-800",
    rose: "bg-rose-100 text-rose-800",
    indigo: "bg-indigo-100 text-indigo-800",
  };
  return (
    <span data-testid={testId} className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${tones[tone]}`}>
      {children}
    </span>
  );
};

const Section = ({ icon: Icon, title, subtitle, testId, children }) => (
  <section data-testid={testId} className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <header className="mb-4 flex items-center gap-3 border-b border-slate-100 pb-3">
      {Icon ? <Icon className="h-5 w-5 text-emerald-700" /> : null}
      <div>
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p> : null}
      </div>
    </header>
    {children}
  </section>
);

const statusTone = (status) => {
  if (status === "mock_offer_ready") return "indigo";
  if (status === "mock_archived" || status === "mock_detected") return "slate";
  if (status === "eligible_pending_permission") return "amber";
  if (status === "eligible_permission_granted_future") return "emerald";
  return "rose";
};

const intentLabel = (intent) => ({
  wants_to_buy: "wants_to_buy",
  wants_to_try: "wants_to_try",
  needs_replacement: "needs_replacement",
  compare_later: "compare_later",
}[intent] || intent);

const AdminGearOfferOpportunities = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const role = user?.role;
  const [selectedId, setSelectedId] = useState(OPPORTUNITIES[0].id);
  if (role !== "admin" && role !== "staff") {
    return <Navigate to="/admin" replace />;
  }

  const selected = OPPORTUNITIES.find((o) => o.id === selectedId) || OPPORTUNITIES[0];

  return (
    <div data-testid="admin-gear-offer-opportunities" className="mx-auto max-w-6xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-semibold text-slate-900">{c.pageTitle}</h1>
        <p className="mt-1 text-xs text-slate-500">{c.pageSubtitle}</p>
      </header>

      <div data-testid="wls3-banner" className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
        <div className="flex items-start gap-3">
          <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700" />
          <div className="text-xs text-amber-900">
            <p className="font-semibold">{c.bannerTitle}</p>
            <p className="mt-1 leading-5">{c.bannerBody}</p>
          </div>
        </div>
      </div>

      <Section icon={Inbox} title={c.section1Title} subtitle={c.section1Subtitle(OPPORTUNITIES.length)} testId="wls3-inbox-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="border-b border-slate-200 text-left text-slate-500">
              <tr>
                <th className="py-1 pr-2">ID</th>
                <th>{c.th_user}</th>
                <th>{c.th_shop}</th>
                <th>{c.th_wishlistItem}</th>
                <th>{c.th_intent}</th>
                <th>{c.th_priority}</th>
                <th>{c.th_status}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {OPPORTUNITIES.map((o) => (
                <tr
                  key={o.id}
                  data-testid={`wls3-opportunity-row-${o.id}`}
                  className={`border-b border-slate-100 hover:bg-slate-50 ${selectedId === o.id ? "bg-emerald-50" : ""}`}
                >
                  <td className="py-1 pr-2 font-mono text-[10px]">{o.id}</td>
                  <td className="text-[11px] text-slate-700">{o.user_mock}</td>
                  <td className="text-[11px] text-slate-700">{o.shop_name}</td>
                  <td className="text-[11px] text-slate-900">{o.wishlist_item}</td>
                  <td><Pill tone="indigo">{intentLabel(o.intent)}</Pill></td>
                  <td>
                    <Pill tone={o.priority === "high" ? "rose" : o.priority === "medium" ? "amber" : "slate"}>
                      {o.priority}
                    </Pill>
                  </td>
                  <td><Pill tone={statusTone(o.status)}>{o.status}</Pill></td>
                  <td className="py-1">
                    <button
                      type="button"
                      data-testid={`wls3-open-detail-${o.id}`}
                      onClick={() => setSelectedId(o.id)}
                      className="rounded border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-100"
                    >
                      {c.openBtn}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section icon={ClipboardList} title={c.section2Title} subtitle={c.section2Subtitle} testId="wls3-statuses">
        <ul className="grid gap-2 sm:grid-cols-2">
          {OPPORTUNITY_STATUSES.map((s) => (
            <li key={s.key} data-testid={`wls3-status-${s.key}`} className="flex items-center justify-between rounded border border-slate-200 bg-white p-2 text-xs">
              <Pill tone={s.tone}>{s.key}</Pill>
              <span className="ml-2 text-[11px] text-slate-600">{c.notes[s.key] || s.note}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Ban} title={c.section3Title} subtitle={c.section3Subtitle} testId="wls3-blocked-actions">
        <table className="w-full text-xs">
          <thead className="border-b border-slate-200 text-left text-slate-500">
            <tr><th className="py-1 pr-2">{c.th_action}</th><th>{c.th_status}</th><th>{c.th_reason}</th></tr>
          </thead>
          <tbody>
            {BLOCKED_ACTIONS.map((a) => (
              <tr key={a.action} data-testid={`wls3-blocked-action-${a.action}`} className="border-b border-slate-100">
                <td className="py-1 pr-2 font-mono text-[11px]">{a.action}</td>
                <td>
                  <Pill tone={a.status === "blocked" ? "rose" : "amber"}>{a.status}</Pill>
                </td>
                <td className="py-1 text-[11px] text-slate-600">{c.blockedReasons[a.action] || a.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-3 flex flex-wrap gap-2" data-testid="wls3-disabled-buttons">
          {BLOCKED_ACTIONS.map((a) => (
            <button
              key={a.action}
              type="button"
              disabled
              data-testid={`wls3-disabled-button-${a.action}`}
              className="cursor-not-allowed rounded border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-medium text-slate-400"
              title={`${a.action} · ${a.status}`}
            >
              {a.action} · {a.status}
            </button>
          ))}
        </div>
      </Section>

      <Section icon={Eye} title={c.section4Title} subtitle={c.section4SubtitleSelected(selected.id)} testId="wls3-detail-panel">
        <div className="grid gap-3 sm:grid-cols-2">
          <div data-testid="wls3-detail-wishlist" className="rounded border border-slate-200 bg-slate-50/50 p-3 text-xs">
            <p className="font-semibold text-slate-900">{c.detail_wishlist}</p>
            <p className="mt-1 text-slate-700">{selected.wishlist_item}</p>
            <p className="mt-0.5 text-[11px] text-slate-500">
              {c.detail_category}: <span className="font-mono">{selected.product_category}</span>
            </p>
            <p className="text-[11px] text-slate-500">
              {c.detail_desired_sku}: <span className="font-mono">{selected.desired_product}</span>
            </p>
          </div>
          <div data-testid="wls3-detail-gear" className="rounded border border-slate-200 bg-slate-50/50 p-3 text-xs">
            <p className="font-semibold text-slate-900">{c.detail_related_gear}</p>
            <p className="mt-1 text-slate-700">{selected.related_gear}</p>
            <p className="mt-0.5 text-[11px] text-slate-500">{c.detail_intent_prefix}: <Pill tone="indigo">{intentLabel(selected.intent)}</Pill></p>
          </div>
          <div data-testid="wls3-detail-shop" className="rounded border border-slate-200 bg-slate-50/50 p-3 text-xs">
            <p className="font-semibold text-slate-900">{c.detail_shop_eligibility}</p>
            <p className="mt-1 text-slate-700">{selected.shop_name} (<span className="font-mono">{selected.shop_id}</span>)</p>
            <p className="mt-0.5"><Pill tone={selected.shop_entitlement === "expired" ? "rose" : selected.shop_entitlement === "paid_active" ? "emerald" : selected.shop_entitlement === "trial_active" ? "amber" : "slate"}>{selected.shop_entitlement}</Pill></p>
          </div>
          <div data-testid="wls3-detail-permission" className="rounded border border-slate-200 bg-slate-50/50 p-3 text-xs">
            <p className="font-semibold text-slate-900">{c.detail_permission}</p>
            <p className="mt-1 flex flex-wrap items-center gap-2">
              <Pill tone={statusTone(selected.permission_state)}>{selected.permission_state}</Pill>
              <Pill tone="slate">{c.detail_consent_prefix}: {selected.consent_state}</Pill>
            </p>
            <p className="mt-1 text-[11px] text-slate-500">
              {c.detail_reason_code}: <span className="font-mono">{selected.reason_code}</span>
            </p>
          </div>
          <div data-testid="wls3-detail-audit-target" className="sm:col-span-2 rounded border border-slate-200 bg-white p-3 text-xs">
            <p className="font-semibold text-slate-900">{c.detail_audit_target_title}</p>
            <p className="mt-1 font-mono text-[11px] text-slate-700">{selected.audit_target_event}</p>
            <p className="mt-1 flex items-center gap-2 rounded bg-rose-50 px-2 py-1 text-[10px] text-rose-800">
              <Lock className="h-3 w-3" />
              <span>{c.detail_audit_not_written}</span>
            </p>
          </div>
        </div>
      </Section>

      <Section icon={FileCheck} title={c.section5Title} subtitle={c.section5Subtitle} testId="wls3-audit-preview">
        <table className="w-full text-xs">
          <thead className="border-b border-slate-200 text-left text-slate-500">
            <tr><th className="py-1 pr-2">{c.th_future_event}</th><th>{c.th_channel}</th><th>{c.th_reason_code}</th></tr>
          </thead>
          <tbody>
            {AUDIT_PREVIEW_EVENTS.map((e) => (
              <tr key={e.event} data-testid={`wls3-audit-event-${e.event}`} className="border-b border-slate-100">
                <td className="py-1 pr-2 font-mono text-[11px]">{e.event}</td>
                <td className="font-mono text-[11px] text-slate-600">{e.channel}</td>
                <td className="font-mono text-[11px] text-slate-600">{e.reason_code}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 flex items-center gap-2 rounded bg-amber-50 px-2 py-1 text-[11px] text-amber-800" data-testid="wls3-audit-preview-disclaimer">
          <AlertTriangle className="h-3 w-3" />
          <span>{c.section5_disclaimer}</span>
        </p>
      </Section>

      <Section icon={Scale} title={c.section6Title} subtitle={c.section6Subtitle} testId="wls3-counsel-hint">
        <ul className="space-y-1 text-xs">
          {c.counselHints.map((h, i) => (
            <li key={i} data-testid={`wls3-counsel-hint-${i}`} className="rounded border border-slate-200 bg-slate-50/40 p-2 text-[11px]">
              {h}
            </li>
          ))}
        </ul>
      </Section>

      <footer data-testid="wls3-footer-legal" className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11px] italic text-slate-600">
        {c.footerLegal}
      </footer>
    </div>
  );
};

export default AdminGearOfferOpportunities;
